import React from 'react'
import isEqual from 'lodash/isEqual'
import { Icon, Text } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'

import { PromotionDetails } from '../../../models/promotion'
import { LineItem, UnknownLineItem } from '../../../models/line-item'
import {
  getVariantsDisplayNames,
  hasVariants,
  isPriceDiscounted,
} from '../../../functions/lineItem'
import { useAppSelector } from '../../../state/hooks'
import {
  selectLineItems,
  selectUnknownLineItems,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { useSpecialOrderProduct } from '../../special-order-product/useSpecialOrderProduct'
import { FulfilmentType } from '../../../models/fulfilment'
import {
  FulfilmentOutletGroup,
  useFulfillmentTakeNowSaleData,
} from '../useFulfillmentTakeNowSaleData'
import { useBasketFulfillmentGroups } from '../useBasketFulfillmentGroups'
import { useSaleHasServiceOrder } from '../useSaleHasServiceOrder'
import { useSaleIsContinuedServiceSale } from '../useSaleIsContinuedServiceSale'
import { LineItemTotalPrice } from './LineItemTotalPrice'
import { LineItemTotalBasePrice } from './LineItemTotalBasePrice'
import { LineItemCustomFields } from './LineItemCustomFields'
import { SpecialOrderProductTag } from './SpecialOrderProductTag'

export const getSaleLineGroupingText = ({
  isContinuedSale,
  fulfillmentType,
}: {
  isContinuedSale: boolean
  fulfillmentType: FulfilmentType
}) => {
  if (fulfillmentType === FulfilmentType.DELIVERY) {
    return isContinuedSale ? 'For delivery' : 'Deliver later'
  }
  if (fulfillmentType === FulfilmentType.PICKUP) {
    return isContinuedSale ? 'For pickup' : 'Pick up later'
  }
  return isContinuedSale ? 'Taken at time of sale' : 'Take now'
}

interface PromotionProps {
  promotionDetails: PromotionDetails[]
}

/**
 * Type guard that distinguishes a known `LineItem` from an `UnknownLineItem`.
 * `SaleSummaryLineItem` dereferences `lineItem.product`, which only exists on
 * known line items, so unknown items must be filtered out before rendering.
 */
const isKnownLineItem = (
  lineItem: LineItem | UnknownLineItem
): lineItem is LineItem => 'product' in lineItem

const LineItemPromotionDetails: React.FC<PromotionProps> = ({
  promotionDetails,
}) => (
  <>
    {promotionDetails.map((promotionDetail, i) => (
      <div
        key={i}
        className="vd-text-supplementary"
        data-cy="sale-summary-promo-data"
      >
        <div>
          <Icon icon="ticket" /> {promotionDetail.name}
        </div>
        {promotionDetail.promoCode && (
          <div className="li-cell-summary-promo-code">
            <Icon icon="key" /> {promotionDetail.promoCode}
          </div>
        )}
      </div>
    ))}
  </>
)

const FulfilmentOutletLabel: React.FC<{ group: FulfilmentOutletGroup }> = ({
  group,
}) => (
  <li
    className="fulfillment-outlet-label"
    data-testid={`fulfillment-outlet-label-${group.outletId}`}
  >
    <Text type="label" className="fulfillment-outlet-label-text">
      {group.outletName}
      {group.isCurrentOutlet && (
        <T
          _str=" (Current)"
          _context="Current outlet label"
          _key="Sale.SummaryLineItems.CurrentOutletLabel"
        />
      )}
    </Text>
  </li>
)

const renderLineItems = (lineItems: LineItem[]) =>
  lineItems.map(lineItem => (
    <SaleSummaryLineItem lineItem={lineItem} key={lineItem.localId} />
  ))

const renderOutletGroups = (groups: FulfilmentOutletGroup[]) =>
  groups.map(group => (
    <React.Fragment key={group.outletId}>
      <FulfilmentOutletLabel group={group} />
      {renderLineItems(group.lines)}
    </React.Fragment>
  ))

export const SaleSummaryLineItems: React.FC = () => {
  // Ensures we don't error out on the sale confirmation view as the sale manager is being initialised
  const lineItems = useAppSelector(selectLineItems, isEqual) || []
  const unknownLineItems = useAppSelector(selectUnknownLineItems, isEqual) || []
  const isServiceSale = useSaleHasServiceOrder()
  const isContinuedServiceSale = useSaleIsContinuedServiceSale()
  const {
    takeNowLines,
    pickupLaterLines,
    deliveryLines,
    fulfilmentOutletGroupsByType,
    shouldShowGroupingOfSaleLines,
    isContinuedSale,
  } = useFulfillmentTakeNowSaleData()

  const {
    deliveryLineItems: unfulfilledDeliveryLines,
    pickUpLineItems: unfulfilledPickupLines,
    nonFulfillmentLineItems: newTakeNowLines,
    fulfilledLineItems,
    shouldUseFulfilledGrouping,
  } = useBasketFulfillmentGroups(
    lineItems,
    unknownLineItems,
    isServiceSale,
    isContinuedServiceSale
  )

  if (!lineItems.length) {
    return null
  }

  const takeNowGroupingText = getSaleLineGroupingText({
    isContinuedSale,
    fulfillmentType: FulfilmentType.UNSPECIFIED,
  })
  const pickupLaterGroupingText = getSaleLineGroupingText({
    isContinuedSale,
    fulfillmentType: FulfilmentType.PICKUP,
  })
  const deliveryGroupingText = getSaleLineGroupingText({
    isContinuedSale,
    fulfillmentType: FulfilmentType.DELIVERY,
  })

  // `fulfilledLineItems` may include `UnknownLineItem`s (unknown items are
  // treated as fulfilled). Filter to known line items before rendering, as
  // `SaleSummaryLineItem` dereferences `lineItem.product` and would throw for
  // unknown items.
  const fulfilledKnownLineItems = fulfilledLineItems.filter(isKnownLineItem)

  // Items added during this continuation with no fulfilment type. Filtered for
  // type-safety only; unknown items are grouped as fulfilled, never here.
  const newTakeNowKnownLineItems = newTakeNowLines.filter(isKnownLineItem)

  // When fulfilled grouping is active, fully fulfilled items (previously
  // taken-now + completed pick-up/delivery) are surfaced under a single
  // "Fulfilled" banner, while items still awaiting pick-up/delivery keep their
  // respective banners and items added during this continuation with no
  // fulfilment type sit under the take-now banner.
  if (shouldUseFulfilledGrouping) {
    return (
      <ul className="sale-summary-list">
        {unfulfilledDeliveryLines.length > 0 ? (
          <>
            <li className="fulfillment-banner" data-testid="delivery-banner">
              <Text
                key={deliveryGroupingText}
                type="label"
                className="banner-text"
              >
                <T
                  _str={deliveryGroupingText}
                  _context="Fulfillment message banner"
                />
              </Text>
            </li>
            {unfulfilledDeliveryLines.map(lineItem => (
              <SaleSummaryLineItem lineItem={lineItem} key={lineItem.localId} />
            ))}
          </>
        ) : null}
        {unfulfilledPickupLines.length > 0 ? (
          <>
            <li className="fulfillment-banner" data-testid="pickup-banner">
              <Text
                key={pickupLaterGroupingText}
                type="label"
                className="banner-text"
              >
                <T
                  _str={pickupLaterGroupingText}
                  _context="Fulfillment message banner"
                />
              </Text>
            </li>
            {unfulfilledPickupLines.map(lineItem => (
              <SaleSummaryLineItem lineItem={lineItem} key={lineItem.localId} />
            ))}
          </>
        ) : null}
        {newTakeNowKnownLineItems.length > 0 ? (
          <>
            <li className="fulfillment-banner" data-testid="take-now-banner">
              <Text
                key={takeNowGroupingText}
                type="label"
                className="banner-text"
              >
                <T
                  _str={takeNowGroupingText}
                  _context="Fulfillment message banner"
                />
              </Text>
            </li>
            {newTakeNowKnownLineItems.map(lineItem => (
              <SaleSummaryLineItem lineItem={lineItem} key={lineItem.localId} />
            ))}
          </>
        ) : null}
        {fulfilledKnownLineItems.length > 0 ? (
          <>
            <li className="fulfillment-banner" data-testid="fulfilled-banner">
              <Text key="Fulfilled" type="label" className="banner-text">
                <T _str="Fulfilled" _context="Fulfillment message banner" />
              </Text>
            </li>
            {fulfilledKnownLineItems.map(lineItem => (
              <SaleSummaryLineItem lineItem={lineItem} key={lineItem.localId} />
            ))}
          </>
        ) : null}
      </ul>
    )
  }

  if (shouldShowGroupingOfSaleLines) {
    return (
      <ul className="sale-summary-list">
        {takeNowLines.length > 0 ? (
          <>
            <li className="fulfillment-banner" data-testid="take-now-banner">
              <Text
                key={takeNowGroupingText}
                type="label"
                className="banner-text"
              >
                <T
                  _str={takeNowGroupingText}
                  _context="Fulfillment message banner"
                />
              </Text>
            </li>
            {renderLineItems(takeNowLines as LineItem[])}
          </>
        ) : null}
        {deliveryLines.length > 0 ? (
          <>
            <li className="fulfillment-banner" data-testid="delivery-banner">
              <Text
                key={deliveryGroupingText}
                type="label"
                className="banner-text"
              >
                <T
                  _str={deliveryGroupingText}
                  _context="Fulfillment message banner"
                />
              </Text>
            </li>
            {fulfilmentOutletGroupsByType
              ? renderOutletGroups(fulfilmentOutletGroupsByType.delivery)
              : renderLineItems(deliveryLines)}
          </>
        ) : null}
        {pickupLaterLines.length > 0 ? (
          <>
            <li className="fulfillment-banner" data-testid="pickup-banner">
              <Text
                key={pickupLaterGroupingText}
                type="label"
                className="banner-text"
              >
                <T
                  _str={pickupLaterGroupingText}
                  _context="Fulfillment message banner"
                />
              </Text>
            </li>
            {fulfilmentOutletGroupsByType
              ? renderOutletGroups(fulfilmentOutletGroupsByType.pickup)
              : renderLineItems(pickupLaterLines)}
          </>
        ) : null}
      </ul>
    )
  }

  return (
    <ul className="sale-summary-list">
      {lineItems.map(lineItem => (
        <SaleSummaryLineItem lineItem={lineItem} key={lineItem.localId} />
      ))}
    </ul>
  )
}

const SaleSummaryLineItemComponent: React.FC<{ lineItem: LineItem }> = ({
  lineItem,
}) => {
  const variantNames = hasVariants(lineItem)
    ? getVariantsDisplayNames(lineItem)
    : ''
  const isSpecialOrderProduct = useSpecialOrderProduct(lineItem)
  const showSpecialOrderProductTag = isSpecialOrderProduct(lineItem)
  return (
    <ul
      className="li-cell li-cell--readonly vd-pt6"
      data-line-item-id={lineItem.localId}
      data-adjusted={lineItem.adjusted}
    >
      <li className="li-cell-summary-container li-cell-summary-container--readonly vd-pt0 vd-pb0">
        <div className="li-cell-summary-left-group">
          <div className="li-cell-summary li-cell-summary--quantity">
            {lineItem.quantity}
          </div>
          <div className="li-cell-summary li-cell-summary--name-container">
            <div className="li-cell-summary pro-li-cell-summary-name">
              {lineItem.product.name}
              {variantNames && (
                <div className="vd-text-supplementary">{variantNames}</div>
              )}
              {lineItem.giftCardNumber && (
                <div className="vd-text-supplementary">
                  Card Number: {lineItem.giftCardNumber}
                </div>
              )}

              {lineItem.promotionDetails && (
                <LineItemPromotionDetails
                  promotionDetails={lineItem.promotionDetails}
                />
              )}

              <LineItemCustomFields lineItem={lineItem} />
            </div>
          </div>
        </div>
        <div className="li-cell-summary-right-group">
          <div className="li-cell-summary li-cell-summary--totals">
            <div className="li-cell-summary--total-price">
              <LineItemTotalPrice type="normal" totals={lineItem.totals} />
            </div>
            {isPriceDiscounted(lineItem) && (
              <div
                className="li-cell-summary--total-base-price"
                data-cy="line-item-total-base-price"
              >
                <LineItemTotalBasePrice totals={lineItem.totals} />
              </div>
            )}
          </div>
        </div>
      </li>
      {lineItem.note && (
        <li className="li-cell-summary-content vd-text-supplementary vd-pt2">
          <div className="wr-honour-line-breaks">Note: {lineItem.note}</div>
        </li>
      )}
      {showSpecialOrderProductTag && (
        <li className="vd-pt2">
          <SpecialOrderProductTag className="li-cell-summary-content" />
        </li>
      )}
    </ul>
  )
}

const SaleSummaryLineItem = React.memo(SaleSummaryLineItemComponent, isEqual)
